import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { apiService } from '../services/api';
import { 
  ArrowLeft, Upload, Trophy, Sparkles, Loader2, X, Trash2, 
  Camera, Medal, Award, Star
} from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIES = [
  { id: 'academic', label: 'Academic', color: 'from-blue-500 to-indigo-600' },
  { id: 'sports', label: 'Sports', color: 'from-orange-500 to-red-600' },
  { id: 'arts', label: 'Arts', color: 'from-pink-500 to-rose-600' },
  { id: 'cultural', label: 'Cultural', color: 'from-purple-500 to-violet-600' },
  { id: 'olympiad', label: 'Olympiad', color: 'from-amber-500 to-yellow-600' },
];

const Competitions = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [viewing, setViewing] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try { const r = await apiService.myCompetitions(); setItems(r.data.competitions); }
    catch (e) { toast.error('Failed'); }
    finally { setLoading(false); }
  };

  const view = async (id) => {
    try { const r = await apiService.getCompetition(id); setViewing(r.data); }
    catch (e) { toast.error('Failed'); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this competition entry?')) return;
    await apiService.deleteCompetition(id);
    toast.success('Deleted');
    load();
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-12 h-12 animate-spin text-amber-500" /></div>;

  return (
    <div className="min-h-screen bg-[#F4F8FB]" data-testid="competitions-page">
      <div className="bg-gradient-to-r from-amber-700 via-yellow-700 to-orange-700 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-amber-400/20 rounded-full blur-3xl -translate-y-1/2"></div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 text-white/80 hover:text-white font-semibold mb-3"><ArrowLeft className="w-5 h-5" />Dashboard</button>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-3xl sm:text-4xl font-black text-white flex items-center gap-3"><Trophy className="w-8 h-8" />My Competitions</h1>
              <p className="text-white/70 text-sm sm:text-base">Upload certificates and achievements from competitions you've participated in.</p>
            </div>
            <motion.button onClick={() => setShowUpload(true)} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} data-testid="upload-competition-btn"
              className="px-5 py-3 bg-white text-amber-700 rounded-2xl font-black flex items-center gap-2 shadow-xl">
              <Upload className="w-5 h-5" />Add Achievement
            </motion.button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        {items.length === 0 ? (
          <div className="bg-white rounded-3xl p-10 text-center border-2 border-slate-100 shadow-md">
            <Trophy className="w-20 h-20 text-amber-300 mx-auto mb-3" />
            <h2 className="text-xl font-black text-[#1E293B] mb-2">No achievements yet</h2>
            <p className="text-[#64748B] mb-5 max-w-md mx-auto">Have you won a quiz, an olympiad, a sports tournament or a cultural event? Upload your certificate to remember and showcase your wins.</p>
            <button onClick={() => setShowUpload(true)} className="px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-2xl font-bold flex items-center gap-2 mx-auto">
              <Upload className="w-5 h-5" />Add Your First Win
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((c, i) => {
              const cat = CATEGORIES.find(x => x.id === c.category) || CATEGORIES[0];
              return (
                <motion.div key={c.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                  className="bg-white rounded-2xl border-2 border-slate-100 shadow-md overflow-hidden hover:shadow-xl transition-all">
                  <div className={`bg-gradient-to-br ${cat.color} p-5 text-white relative`}>
                    <Medal className="w-10 h-10 mb-2 opacity-90" />
                    <h3 className="font-black text-lg leading-tight line-clamp-2">{c.title}</h3>
                    <span className="text-xs uppercase font-bold opacity-80 mt-1 inline-block">{cat.label}</span>
                  </div>
                  <div className="p-4 space-y-2">
                    {c.achievement && <p className="text-sm font-bold text-amber-700 flex items-center gap-1"><Award className="w-4 h-4" />{c.achievement}</p>}
                    {c.description && <p className="text-xs text-[#64748B] line-clamp-2">{c.description}</p>}
                    <p className="text-xs text-slate-400">{new Date(c.created_at).toLocaleDateString()}</p>
                    <button onClick={() => view(c.id)} className="w-full py-2 bg-amber-100 text-amber-700 rounded-lg font-bold text-sm hover:bg-amber-200">View Certificate</button>
                    <button onClick={() => del(c.id)} className="w-full py-1.5 bg-red-50 text-red-600 rounded-lg font-bold text-xs flex items-center justify-center gap-1.5 hover:bg-red-100">
                      <Trash2 className="w-3 h-3" />Delete
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      <AnimatePresence>
        {showUpload && <UploadModal onClose={() => setShowUpload(false)} onDone={() => { setShowUpload(false); load(); }} />}
        {viewing && <ViewModal item={viewing} onClose={() => setViewing(null)} />}
      </AnimatePresence>
    </div>
  );
};

const UploadModal = ({ onClose, onDone }) => {
  const [form, setForm] = useState({ title: '', category: 'academic', description: '', achievement: '' });
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef();

  const handleFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (!f.type.startsWith('image/')) { toast.error('Image only'); return; }
    if (f.size > 10 * 1024 * 1024) { toast.error('Max 10MB'); return; }
    setFile(f); setPreview(URL.createObjectURL(f));
  };

  const submit = async () => {
    if (!form.title || !file) { toast.error('Title and image required'); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append('image', file);
      await apiService.uploadCompetition(fd);
      toast.success('Achievement saved!');
      onDone();
    } catch (e) { toast.error('Upload failed'); }
    finally { setSubmitting(false); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <motion.div initial={{ y: 200 }} animate={{ y: 0 }} exit={{ y: 200 }}
        className="bg-white rounded-t-3xl sm:rounded-3xl max-w-md w-full max-h-[90vh] overflow-y-auto p-6 shadow-2xl">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-black text-[#1E293B]">Add Achievement</h2>
          <button onClick={onClose} className="text-slate-400"><X className="w-5 h-5" /></button>
        </div>
        
        <div className="space-y-3">
          <input type="text" placeholder="Competition title (e.g. Math Olympiad 2025)" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
            className="w-full p-3 bg-slate-50 border-2 border-slate-200 rounded-xl font-medium text-sm focus:border-amber-500 outline-none" data-testid="comp-title" />
          
          <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
            className="w-full p-3 bg-slate-50 border-2 border-slate-200 rounded-xl font-medium text-sm" data-testid="comp-category">
            {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
          
          <input type="text" placeholder="Achievement (e.g. 1st place, Gold medal)" value={form.achievement} onChange={e => setForm({ ...form, achievement: e.target.value })}
            className="w-full p-3 bg-slate-50 border-2 border-slate-200 rounded-xl font-medium text-sm focus:border-amber-500 outline-none" data-testid="comp-achievement" />
          
          <textarea placeholder="Description (optional)" rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
            className="w-full p-3 bg-slate-50 border-2 border-slate-200 rounded-xl font-medium text-sm resize-none focus:border-amber-500 outline-none" data-testid="comp-description" />
          
          {preview ? (
            <div className="relative">
              <img src={preview} alt="cert" className="w-full rounded-2xl border-2 border-slate-200" />
              <button onClick={() => { setFile(null); setPreview(null); }} className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center">
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button onClick={() => fileRef.current?.click()} data-testid="comp-upload"
              className="w-full p-6 bg-amber-50 border-2 border-dashed border-amber-300 rounded-2xl flex flex-col items-center gap-2 hover:border-amber-500">
              <Camera className="w-10 h-10 text-amber-600" />
              <span className="font-bold text-amber-700 text-sm">Add Certificate Photo</span>
            </button>
          )}
          <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden" />
          
          <button onClick={submit} disabled={submitting} data-testid="comp-submit"
            className="w-full py-3 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-xl font-bold disabled:opacity-50 flex items-center justify-center gap-2">
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin" />Saving...</> : <><Sparkles className="w-4 h-4" />Save Achievement</>}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

const ViewModal = ({ item, onClose }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
    <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} onClick={e => e.stopPropagation()}
      className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-5 shadow-2xl">
      <div className="flex justify-between items-start mb-3">
        <div>
          <h2 className="text-xl font-black text-[#1E293B]">{item.title}</h2>
          {item.achievement && <p className="text-sm font-bold text-amber-700 flex items-center gap-1 mt-1"><Star className="w-4 h-4" />{item.achievement}</p>}
        </div>
        <button onClick={onClose} className="text-slate-400"><X className="w-5 h-5" /></button>
      </div>
      <img src={`data:image/jpeg;base64,${item.image_base64}`} alt={item.title} className="w-full rounded-2xl border-2 border-slate-200 mb-3" />
      {item.description && <p className="text-sm text-[#64748B]">{item.description}</p>}
    </motion.div>
  </motion.div>
);

export default Competitions;
